import { useState, useEffect } from 'react'
import {
  UserPlus, Download, AlertTriangle, Check, Copy, RefreshCw, Users, ShieldAlert,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import PageHeader from '@/components/PageHeader'
import { userAdminAPI } from '@/lib/api'
import CredentialCards, { bewaarCredentials, leesCredentials, wisCredentials } from '@/components/CredentialCards'

/* Users importeren uit ClubKassa.

   Twee stappen met opzet: eerst kijken wat er zou gebeuren, dan pas uitvoeren.
   De tijdelijke wachtwoorden zijn éénmalig zichtbaar — daarom krijgen ze na de
   import een eigen blok met een kopieerknop. */

export default function UserImportPage() {
  const [preview, setPreview] = useState(null)
  const [result, setResult]   = useState(null)
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState(null)
  const [copied, setCopied]   = useState(false)
  // Set van geselecteerde nieuw-kandidaten (key = sync_id of `naam:<name>`
  // als sync_id ontbreekt). Standaard alles aan zodat "gewoon importeren"
  // hetzelfde gedrag houdt als voorheen.
  const [selectie, setSelectie] = useState(new Set())
  // Wachtwoorden uit een eerdere import in deze tab — vangnet tegen een refresh.
  const [hersteld, setHersteld] = useState(() => leesCredentials())
  const [branding, setBranding] = useState(null)

  useEffect(() => {
    fetch('/api/settings/branding')
      .then(r => r.json())
      .then(d => setBranding(d?.branding ?? null))
      .catch(() => {})
  }, [])

  // De accounts die kaartjes moeten krijgen: vers geïmporteerd, of herstelde.
  const kaartjesVoor = result?.aangemaakt?.length ? result.aangemaakt : (hersteld?.lijst ?? [])

  // Elke nieuw-kandidaat een unieke key geven voor de selectie-set.
  const keyVoor = (u) => u.sync_id ? `sync:${u.sync_id}` : `naam:${u.name}`

  const doPreview = async () => {
    setBusy(true); setError(null); setResult(null)
    try {
      const { data } = await userAdminAPI.previewImport()
      setPreview(data)
      // Start met alles aan zodat het default-gedrag "importeer alle nieuwen"
      // blijft. Admin kan gericht uitzetten wat hij overslaat.
      setSelectie(new Set((data?.nieuw || []).map(keyVoor)))
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
    } finally { setBusy(false) }
  }

  const doImport = async () => {
    const gekozen = preview.nieuw.filter(u => selectie.has(keyVoor(u)))
    if (gekozen.length === 0) {
      window.alert('Geen kandidaten geselecteerd.')
      return
    }
    if (!window.confirm(`${gekozen.length} nieuwe account${gekozen.length === 1 ? '' : 's'} aanmaken. Doorgaan?`)) return
    setBusy(true); setError(null)
    try {
      const sync_ids = gekozen.filter(u => u.sync_id).map(u => u.sync_id)
      const names    = gekozen.filter(u => !u.sync_id).map(u => u.name)
      const { data } = await userAdminAPI.runImport({ sync_ids, names })
      setResult(data)
      setPreview(null)
      // Meteen veiligstellen: een refresh mag deze wachtwoorden niet wissen.
      if (data?.aangemaakt?.length) {
        bewaarCredentials(data.aangemaakt)
        setHersteld(null)
      }
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
    } finally { setBusy(false) }
  }

  const copyCredentials = () => {
    const text = result.aangemaakt
      .map(u => `${u.display_name}\t${u.username}\t${u.tijdelijk_wachtwoord}`)
      .join('\n')
    navigator.clipboard?.writeText(`Naam\tGebruikersnaam\tWachtwoord\n${text}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div>
      <PageHeader
        icon={UserPlus}
        title="Deelnemers importeren"
        subtitle="Haal iedereen op uit ClubKassa — inclusief hun RFID-kaart"
      />

      {!result && hersteld && (
        <Card className="mb-4" style={{ borderLeft: '2px solid #E4C265' }}>
          <CardContent className="p-4 flex items-start gap-3 flex-wrap">
            <ShieldAlert className="h-5 w-5 text-gold shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[14rem]">
              <div className="font-semibold text-sm">
                Nog {hersteld.lijst.length} wachtwoorden van een eerdere import
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Bewaard in deze tab op {new Date(hersteld.ts).toLocaleString('nl-BE')}.
                Ze verdwijnen zodra je de tab sluit.
              </div>
            </div>
            <Button size="sm" variant="ghost" className="text-muted-foreground"
                    onClick={() => { wisCredentials(); setHersteld(null) }}>
              Wissen
            </Button>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="mb-4" style={{ borderLeft: '2px solid #FF5A5A' }}>
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-signal shrink-0 mt-0.5" />
            <div className="text-sm">{error}</div>
          </CardContent>
        </Card>
      )}

      {/* Stap 1 */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[16rem]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Stap 1</div>
              <div className="font-semibold text-sm">Kijken wat er zou gebeuren</div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Vergelijkt ClubKassa met de deelnemers hier. Er wordt nog niets gewijzigd.
              </p>
            </div>
            <Button onClick={doPreview} disabled={busy}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />
              Vergelijken
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview-uitkomst */}
      {preview && (
        <>
          <div className="grid gap-3 grid-cols-3 mb-4">
            <CountTile value={preview.nieuw.length}        label="nieuw"        accent="#B4FF39" />
            <CountTile value={preview.bestaat.length}      label="bestaat al"   accent="#7BC8FF" />
            <CountTile value={preview.overgeslagen.length} label="overgeslagen" accent="#94A3B8" />
          </div>

          {preview.nieuw.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <CardTitle className="text-base">
                    Wordt aangemaakt ({selectie.size}/{preview.nieuw.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost"
                            onClick={() => setSelectie(new Set(preview.nieuw.map(keyVoor)))}>
                      Alles aan
                    </Button>
                    <Button size="sm" variant="ghost"
                            onClick={() => setSelectie(new Set())}>
                      Alles uit
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {preview.nieuw.map((u, i) => {
                    const key = keyVoor(u)
                    const on = selectie.has(key)
                    return (
                      <label key={i}
                             className={`flex items-center gap-3 text-sm py-1.5 px-2 border-b border-border/40 last:border-0 cursor-pointer rounded ${
                               on ? '' : 'opacity-50'}`}>
                        <input type="checkbox" checked={on}
                               onChange={e => {
                                 const nieuw = new Set(selectie)
                                 if (e.target.checked) nieuw.add(key); else nieuw.delete(key)
                                 setSelectie(nieuw)
                               }} />
                        <span className="flex-1 min-w-0 truncate">{u.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{u.username}</span>
                        {u.rfid && <span className="font-mono text-[10px] uppercase tracking-widest text-acid">rfid</span>}
                      </label>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {preview.bestaat.length > 0 && (
            <Card className="mb-4" style={{ borderLeft: '2px solid #7BC8FF' }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Check className="h-4 w-4 text-cyan-400" />
                  Bestaat al ({preview.bestaat.length})
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Deze deelnemers zijn al gekoppeld via RFID of kassa-ID. Er wordt niks
                  aan hun account gewijzigd.
                </p>
              </CardHeader>
              <CardContent>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {preview.bestaat.map((u, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b border-border/40 last:border-0">
                      <span className="flex-1 min-w-0 truncate">{u.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">@{u.username}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                            title={u.via === 'rfid'
                                     ? 'Match via RFID-kaart'
                                     : u.via === 'sync'
                                       ? 'Match via kassa-ID (uit vorige import)'
                                       : 'Match via RFID én kassa-ID'}>
                        via {u.via || 'rfid'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {preview.overgeslagen.length > 0 && (
            <Card className="mb-4" style={{ borderLeft: '2px solid #94A3B8' }}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  Overgeslagen ({preview.overgeslagen.length})
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Deze rijen worden niet geïmporteerd. Meestal komt het door
                  "uitgeschakeld in kassa" of een ontbrekende naam.
                </p>
              </CardHeader>
              <CardContent>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {preview.overgeslagen.map((u, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b border-border/40 last:border-0">
                      <span className="flex-1 min-w-0 truncate">{u.name || <span className="italic text-muted-foreground">— (geen naam)</span>}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {u.reden}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="mb-4" style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[16rem]">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Stap 2</div>
                  <div className="font-semibold text-sm">Import uitvoeren</div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Elk nieuw account krijgt een tijdelijk wachtwoord dat de deelnemer bij de
                    eerste login zelf moet vervangen. Bestaande accounts blijven ongewijzigd.
                  </p>
                </div>
                <Button onClick={doImport} disabled={busy || selectie.size === 0}>
                  <Download className="h-4 w-4 mr-1.5" />
                  {selectie.size} aanmaken
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Resultaat met de eenmalig zichtbare wachtwoorden */}
      {result && (
        <>
          <Card className="mb-4" style={{ borderLeft: '2px solid #61E294' }}>
            <CardContent className="p-4 flex items-start gap-3">
              <Check className="h-5 w-5 text-live shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-sm">Import klaar</div>
                <div className="text-sm text-muted-foreground">{result.samenvatting}</div>
              </div>
            </CardContent>
          </Card>

          {result.aangemaakt.length > 0 && (
            <Card style={{ borderLeft: '2px solid #E4C265' }}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 text-gold" />
                      Tijdelijke wachtwoorden
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1 max-w-lg leading-relaxed">
                      Deze lijst is nu voor het laatst te zien — de wachtwoorden staan versleuteld
                      in de database en zijn later niet meer op te vragen. Kopieer ze eerst.
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={copyCredentials}>
                    {copied ? <Check className="h-4 w-4 mr-1.5 text-live" /> : <Copy className="h-4 w-4 mr-1.5" />}
                    {copied ? 'Gekopieerd' : 'Kopieer als tabel'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <th className="text-left pb-2">Naam</th>
                        <th className="text-left pb-2">Gebruikersnaam</th>
                        <th className="text-left pb-2">Wachtwoord</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.aangemaakt.map(u => (
                        <tr key={u.id} className="border-b border-border/40 last:border-0">
                          <td className="py-1.5 pr-3">{u.display_name}</td>
                          <td className="py-1.5 pr-3 font-mono text-xs">{u.username}</td>
                          <td className="py-1.5 font-mono text-xs text-acid">{u.tijdelijk_wachtwoord}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {kaartjesVoor.length > 0 && (
        <CredentialCards
          accounts={kaartjesVoor}
          siteName={branding?.site_name || 'LAN Server'}
          siteUrl={typeof window !== 'undefined' ? window.location.host : 'lanserver.local'}
          onClear={() => { setResult(null); setHersteld(null) }}
        />
      )}

      {!preview && !result && !hersteld && !busy && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Users className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              Begin met "Vergelijken" om te zien wie er nog ontbreekt.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function CountTile({ value, label, accent }) {
  return (
    <Card style={{ borderLeft: `2px solid ${accent}` }}>
      <CardContent className="p-4">
        <div className="text-2xl font-bold tabular-nums leading-none">{value}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
      </CardContent>
    </Card>
  )
}
